/**
 * Region-aware paywall content system.
 *
 * Each paywall has two tabs:
 *  1. WHY UPGRADE — value proposition with stats, features, and case studies
 *  2. HOW TO GET ACCESS — steps and support info
 *
 * Both vary by region (TX, CA, or default).
 * The general message varies by credit state (limit reached vs. sessions remaining).
 *
 * All user-facing strings are wrapped in Tolgee t() for localization.
 * Strings containing <strong> tags are rendered via dangerouslySetInnerHTML in the component.
 */

import { useTranslate } from '@tolgee/react';
import { useMemo } from 'react';

export type PaywallRegion = 'TX' | 'CA' | 'OTHER' | 'INTL';

// ─── Types ───────────────────────────────────────────────────────────

export interface WhyUpgradeContent {
  statPercent: string;
  statHeadline: string;
  statSubtext: string;
  caseStudies: { label: string; url: string }[];
  features: string[];
}

export interface HowToGetAccessContent {
  intro: string;
  notAloneTitle: string;
  notAlone: string;
  steps: { title: string; description: string }[];
  weCanHelpTitle: string;
  weCanHelp: string[];
}

// ─── Region resolver ─────────────────────────────────────────────────

export function resolveRegion(region?: string, country?: string): PaywallRegion {
  if (country && country.toUpperCase() !== 'US') return 'INTL';
  if (!region) return 'OTHER';
  const upper = region.toUpperCase();
  if (upper === 'TX') return 'TX';
  if (upper === 'CA') return 'CA';
  return 'OTHER';
}

// ─── Hook ────────────────────────────────────────────────────────────

export function usePaywallContent(region: PaywallRegion) {
  const { t } = useTranslate();

  const whyUpgrade = useMemo((): WhyUpgradeContent => {
    switch (region) {
      case 'TX':
        return {
          statPercent: '30%',
          statHeadline: t(
            'LessonLimitPaywall.WhyUpgrade.TX.StatHeadline',
            'Growth in Meets and Masters on STAAR!',
          ),
          statSubtext: t(
            'LessonLimitPaywall.WhyUpgrade.TX.StatSubtext',
            'Teachers using Curipod 3+ times per week see double-digit growth in students\u2019 STAAR scores.',
          ),
          caseStudies: [
            {
              label: t(
                'LessonLimitPaywall.WhyUpgrade.TX.CaseStudy1Label',
                'Efficacy & Impact',
              ),
              url: 'https://curipod.com/case-studies/curipod-case-study-green-valley',
            },
            {
              label: t(
                'LessonLimitPaywall.WhyUpgrade.TX.CaseStudy2Label',
                'Real-Time Feedback, Real Results',
              ),
              url: 'https://curipod.com/case-studies/curipod-case-study-south-east-texas',
            },
          ],
          features: [
            t('LessonLimitPaywall.WhyUpgrade.TX.Feature1', 'Unlimited teaching sessions'),
            t('LessonLimitPaywall.WhyUpgrade.TX.Feature2', 'STAAR prep included'),
            t(
              'LessonLimitPaywall.WhyUpgrade.TX.Feature3',
              '3,000+ schools already on the plan',
            ),
          ],
        };

      case 'CA':
        return {
          statPercent: '40%',
          statHeadline: t(
            'LessonLimitPaywall.WhyUpgrade.CA.StatHeadline',
            'Higher writing scores on CAASPP!',
          ),
          statSubtext: t(
            'LessonLimitPaywall.WhyUpgrade.CA.StatSubtext',
            'Teachers using Curipod 3+ times per week see double-digit growth in students\u2019 CAASPP scores.',
          ),
          caseStudies: [
            {
              label: t(
                'LessonLimitPaywall.WhyUpgrade.CA.CaseStudy1Label',
                'Efficacy & Impact',
              ),
              url: 'https://curipod.com/case-studies/california-writing-achievement',
            },
            {
              label: t(
                'LessonLimitPaywall.WhyUpgrade.CA.CaseStudy2Label',
                'Southern CA \u2013 Transformative Writing',
              ),
              url: 'https://curipod.com/case-studies/curipod-case-study-southern-ca',
            },
          ],
          features: [
            t('LessonLimitPaywall.WhyUpgrade.CA.Feature1', 'Unlimited teaching sessions'),
            t(
              'LessonLimitPaywall.WhyUpgrade.CA.Feature2',
              'CAASPP & ELPAC prep included',
            ),
            t(
              'LessonLimitPaywall.WhyUpgrade.CA.Feature3',
              '3,000+ schools already on the plan',
            ),
          ],
        };

      case 'INTL':
        return {
          statPercent: '40%',
          statHeadline: t(
            'LessonLimitPaywall.WhyUpgrade.INTL.StatHeadline',
            'Higher writing scores with Curipod!',
          ),
          statSubtext: t(
            'LessonLimitPaywall.WhyUpgrade.INTL.StatSubtext',
            'Teachers who use Curipod 3+ times per week see significant growth in students\u2019 writing outcomes.',
          ),
          caseStudies: [
            {
              label: t(
                'LessonLimitPaywall.WhyUpgrade.INTL.CaseStudy1Label',
                'Upskills Teachers & Reduces Planning Time',
              ),
              url:
                'https://curipod.com/c/blog-posts/curipod-upskills-new-teachers-and-reduces-planning-time-by-30',
            },
            {
              label: t(
                'LessonLimitPaywall.WhyUpgrade.INTL.CaseStudy2Label',
                'Increasing Science Class Engagement',
              ),
              url:
                'https://curipod.com/c/blog-posts/increasing-science-class-engagement-with-curipod',
            },
          ],
          features: [
            t(
              'LessonLimitPaywall.WhyUpgrade.INTL.Feature1',
              'Unlimited teaching sessions',
            ),
            t(
              'LessonLimitPaywall.WhyUpgrade.INTL.Feature2',
              'AI-powered writing feedback',
            ),
            t(
              'LessonLimitPaywall.WhyUpgrade.INTL.Feature3',
              '3,000+ schools already on the plan',
            ),
          ],
        };

      default:
        return {
          statPercent: '40%',
          statHeadline: t(
            'LessonLimitPaywall.WhyUpgrade.OTHER.StatHeadline',
            'Higher writing scores on state test scores!',
          ),
          statSubtext: t(
            'LessonLimitPaywall.WhyUpgrade.OTHER.StatSubtext',
            'Teachers who use Curipod 3+ times per week see double-digit growth in students\u2019 state test scores.',
          ),
          caseStudies: [
            {
              label: t(
                'LessonLimitPaywall.WhyUpgrade.OTHER.CaseStudy1Label',
                'Efficacy & Impact',
              ),
              url: 'https://curipod.com/case-studies/curipod-case-study-green-valley',
            },
            {
              label: t(
                'LessonLimitPaywall.WhyUpgrade.OTHER.CaseStudy2Label',
                'Writing Achievement',
              ),
              url: 'https://curipod.com/case-studies/california-writing-achievement',
            },
          ],
          features: [
            t(
              'LessonLimitPaywall.WhyUpgrade.OTHER.Feature1',
              'Unlimited teaching sessions',
            ),
            t(
              'LessonLimitPaywall.WhyUpgrade.OTHER.Feature2',
              'Writing practice across all subjects',
            ),
            t(
              'LessonLimitPaywall.WhyUpgrade.OTHER.Feature3',
              '3,000+ schools already on the plan',
            ),
          ],
        };
    }
  }, [region, t]);

  const howToGetAccess = useMemo((): HowToGetAccessContent => {
    switch (region) {
      case 'TX':
        return {
          intro: t(
            'LessonLimitPaywall.HowToGetAccess.TX.Intro',
            "Curipod's paid plan is purchased by schools. We do not have a paid individual teacher plan. We know this process can take time, and we have a <strong>dedicated team here to help you</strong>.",
          ),
          notAloneTitle: t(
            'LessonLimitPaywall.HowToGetAccess.TX.NotAloneTitle',
            'You are not alone!',
          ),
          notAlone: t(
            'LessonLimitPaywall.HowToGetAccess.TX.NotAlone',
            'Our team has helped hundreds of teachers in TX get access to the Curipod school plan! With our many case studies documenting <strong>double-digit STAAR score growth</strong>, AI-driven writing feedback, and TELPAS support features, we can help schools leverage <strong>different funding sources</strong> to purchase Curipod.',
          ),
          steps: [
            {
              title: t(
                'LessonLimitPaywall.HowToGetAccess.TX.Step1Title',
                'Invite your principal to your classroom to observe Curipod in action',
              ),
              description: t(
                'LessonLimitPaywall.HowToGetAccess.TX.Step1Description',
                'Show how your students engage and learn with Curipod lessons.',
              ),
            },
            {
              title: t(
                'LessonLimitPaywall.HowToGetAccess.TX.Step2Title',
                "Show your principal your students' progress",
              ),
              description: t(
                'LessonLimitPaywall.HowToGetAccess.TX.Step2Description',
                'How are scores improving? How many students are getting better results?',
              ),
            },
            {
              title: t(
                'LessonLimitPaywall.HowToGetAccess.TX.Step3Title',
                'Share our case studies with your admin',
              ),
              description: t(
                'LessonLimitPaywall.HowToGetAccess.TX.Step3Description',
                'Curipod is proven to increase student achievement by dozens of campuses across Texas.',
              ),
            },
            {
              title: t(
                'LessonLimitPaywall.HowToGetAccess.TX.Step4Title',
                'Book a meeting with us to help you',
              ),
              description: t(
                'LessonLimitPaywall.HowToGetAccess.TX.Step4Description',
                'Bonus to bring your principal or instructional coach, but we can also support you alone \u{1F60A}',
              ),
            },
          ],
          weCanHelpTitle: t(
            'LessonLimitPaywall.HowToGetAccess.TX.WeCanHelpTitle',
            'We can help you with:',
          ),
          weCanHelp: [
            t(
              'LessonLimitPaywall.HowToGetAccess.TX.WeCanHelp1',
              'Finding funding sources similar schools use for Curipod.',
            ),
            t(
              'LessonLimitPaywall.HowToGetAccess.TX.WeCanHelp2',
              'Sharing how other teachers got their school to implement Curipod.',
            ),
            t(
              'LessonLimitPaywall.HowToGetAccess.TX.WeCanHelp3',
              'Answering any questions you might have.',
            ),
          ],
        };

      case 'CA':
        return {
          intro: t(
            'LessonLimitPaywall.HowToGetAccess.CA.Intro',
            "Curipod's paid plan is purchased by schools. We do not have a paid individual teacher plan. We know this process can take time, and we have a <strong>dedicated team here to help you</strong>.",
          ),
          notAloneTitle: t(
            'LessonLimitPaywall.HowToGetAccess.CA.NotAloneTitle',
            'You are not alone!',
          ),
          notAlone: t(
            'LessonLimitPaywall.HowToGetAccess.CA.NotAlone',
            'Our team has helped hundreds of teachers in CA get access to the Curipod school plan! With our many case studies documenting <strong>double-digit CAASPP score growth</strong>, AI-driven writing feedback, and English learner support features, we can help schools leverage <strong>different funding sources</strong> to purchase Curipod.',
          ),
          steps: [
            {
              title: t(
                'LessonLimitPaywall.HowToGetAccess.CA.Step1Title',
                'Invite your principal to your classroom to observe Curipod in action',
              ),
              description: t(
                'LessonLimitPaywall.HowToGetAccess.CA.Step1Description',
                'Show how your students engage and learn with Curipod lessons.',
              ),
            },
            {
              title: t(
                'LessonLimitPaywall.HowToGetAccess.CA.Step2Title',
                "Show your principal your students' progress",
              ),
              description: t(
                'LessonLimitPaywall.HowToGetAccess.CA.Step2Description',
                'How are scores improving? How many students are getting better results?',
              ),
            },
            {
              title: t(
                'LessonLimitPaywall.HowToGetAccess.CA.Step3Title',
                'Share our case studies with your admin',
              ),
              description: t(
                'LessonLimitPaywall.HowToGetAccess.CA.Step3Description',
                'Curipod is proven to increase student achievement by dozens of campuses across California.',
              ),
            },
            {
              title: t(
                'LessonLimitPaywall.HowToGetAccess.CA.Step4Title',
                'Book a meeting with us to help you',
              ),
              description: t(
                'LessonLimitPaywall.HowToGetAccess.CA.Step4Description',
                'Bonus to bring your principal or instructional coach, but we can also support you alone \u{1F60A}',
              ),
            },
          ],
          weCanHelpTitle: t(
            'LessonLimitPaywall.HowToGetAccess.CA.WeCanHelpTitle',
            'We can help you with:',
          ),
          weCanHelp: [
            t(
              'LessonLimitPaywall.HowToGetAccess.CA.WeCanHelp1',
              'Finding funding sources similar schools use for Curipod.',
            ),
            t(
              'LessonLimitPaywall.HowToGetAccess.CA.WeCanHelp2',
              'Sharing how other teachers got their school to implement Curipod.',
            ),
            t(
              'LessonLimitPaywall.HowToGetAccess.CA.WeCanHelp3',
              'Answering any questions you might have.',
            ),
          ],
        };

      case 'INTL':
        return {
          intro: t(
            'LessonLimitPaywall.HowToGetAccess.INTL.Intro',
            'Get full access to Curipod for you or your whole school. Visit our pricing page to explore plans and get started today.',
          ),
          notAloneTitle: t(
            'LessonLimitPaywall.HowToGetAccess.INTL.NotAloneTitle',
            'Teachers worldwide love Curipod!',
          ),
          notAlone: t(
            'LessonLimitPaywall.HowToGetAccess.INTL.NotAlone',
            'Thousands of teachers across 90+ countries use Curipod to boost student engagement and improve writing outcomes. Join them with a plan that fits your needs.',
          ),
          steps: [
            {
              title: t(
                'LessonLimitPaywall.HowToGetAccess.INTL.Step1Title',
                'Explore our plans',
              ),
              description: t(
                'LessonLimitPaywall.HowToGetAccess.INTL.Step1Description',
                'Visit the pricing page to see what fits your school or classroom.',
              ),
            },
            {
              title: t(
                'LessonLimitPaywall.HowToGetAccess.INTL.Step2Title',
                'Choose your plan',
              ),
              description: t(
                'LessonLimitPaywall.HowToGetAccess.INTL.Step2Description',
                'Pick between individual or school-wide access.',
              ),
            },
            {
              title: t(
                'LessonLimitPaywall.HowToGetAccess.INTL.Step3Title',
                'Get started instantly',
              ),
              description: t(
                'LessonLimitPaywall.HowToGetAccess.INTL.Step3Description',
                'Unlock unlimited teaching sessions and start creating engaging lessons right away \u{1F60A}',
              ),
            },
          ],
          weCanHelpTitle: t(
            'LessonLimitPaywall.HowToGetAccess.INTL.WeCanHelpTitle',
            'Need help deciding?',
          ),
          weCanHelp: [
            t(
              'LessonLimitPaywall.HowToGetAccess.INTL.WeCanHelp1',
              'Chat with us to find the right plan for your needs.',
            ),
            t(
              'LessonLimitPaywall.HowToGetAccess.INTL.WeCanHelp2',
              'Get volume pricing for your school or district.',
            ),
          ],
        };

      default:
        return {
          intro: t(
            'LessonLimitPaywall.HowToGetAccess.OTHER.Intro',
            "Curipod's paid plan is purchased by schools. We do not have a paid individual teacher plan. We know this process can take time, and we have a <strong>dedicated team here to help you</strong>.",
          ),
          notAloneTitle: t(
            'LessonLimitPaywall.HowToGetAccess.OTHER.NotAloneTitle',
            'You are not alone!',
          ),
          notAlone: t(
            'LessonLimitPaywall.HowToGetAccess.OTHER.NotAlone',
            'Our team supports teachers with this every day! With our many case studies documenting <strong>double-digit growth in writing scores</strong>, AI-driven writing feedback, we can help schools leverage <strong>different funding sources</strong> to purchase Curipod.',
          ),
          steps: [
            {
              title: t(
                'LessonLimitPaywall.HowToGetAccess.OTHER.Step1Title',
                'Invite your principal to your classroom to observe Curipod in action',
              ),
              description: t(
                'LessonLimitPaywall.HowToGetAccess.OTHER.Step1Description',
                'Show how your students engage and learn with Curipod lessons.',
              ),
            },
            {
              title: t(
                'LessonLimitPaywall.HowToGetAccess.OTHER.Step2Title',
                "Show your principal your students' progress",
              ),
              description: t(
                'LessonLimitPaywall.HowToGetAccess.OTHER.Step2Description',
                'How are scores improving? How many students are getting better results?',
              ),
            },
            {
              title: t(
                'LessonLimitPaywall.HowToGetAccess.OTHER.Step3Title',
                'Share our case studies with your admin',
              ),
              description: t(
                'LessonLimitPaywall.HowToGetAccess.OTHER.Step3Description',
                'Curipod is proven to increase student achievement by dozens of campuses like yours.',
              ),
            },
            {
              title: t(
                'LessonLimitPaywall.HowToGetAccess.OTHER.Step4Title',
                'Book a meeting with us to help you',
              ),
              description: t(
                'LessonLimitPaywall.HowToGetAccess.OTHER.Step4Description',
                'Bonus to bring your principal or instructional coach, but we can also support you alone \u{1F60A}',
              ),
            },
          ],
          weCanHelpTitle: t(
            'LessonLimitPaywall.HowToGetAccess.OTHER.WeCanHelpTitle',
            'We can help you with:',
          ),
          weCanHelp: [
            t(
              'LessonLimitPaywall.HowToGetAccess.OTHER.WeCanHelp1',
              'Finding funding sources similar schools use for Curipod.',
            ),
            t(
              'LessonLimitPaywall.HowToGetAccess.OTHER.WeCanHelp2',
              'Sharing how other teachers got their school to implement Curipod.',
            ),
            t(
              'LessonLimitPaywall.HowToGetAccess.OTHER.WeCanHelp3',
              'Answering any questions you might have.',
            ),
          ],
        };
    }
  }, [region, t]);

  return { whyUpgrade, howToGetAccess };
}
